import React, { useEffect, useState } from 'react';
import { addDoc, deleteDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { wishlistColl, wishlistDoc } from '../db';
import { Screen, UserProfile } from '../App';

interface Props { user: UserProfile; navigate: (s: Screen) => void; backTo?: Screen; }

interface Wish {
  id: string;
  title: string;
  hint: string;
  link: string;
  priority: string;
  fulfilled: boolean;
  createdBy: string;
  createdByName: string;
}

const PRIORITIES = ['Tatlı fikir', 'Çok isterim', 'Sürpriz olabilir'];

export default function GiftWishlist({ user, navigate, backTo = 'us' }: Props) {
  const [wishes, setWishes] = useState<Wish[]>([]);
  const [title, setTitle] = useState('');
  const [hint, setHint] = useState('');
  const [link, setLink] = useState('');
  const [priority, setPriority] = useState(PRIORITIES[0]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = query(wishlistColl(user.coupleId), orderBy('timestamp', 'desc'));
    return onSnapshot(q, snap => setWishes(snap.docs.map(d => ({ id: d.id, ...d.data() } as Wish))));
  }, [user.coupleId]);

  const add = async () => {
    if (!title.trim()) return;
    await addDoc(wishlistColl(user.coupleId), {
      title: title.trim(),
      hint: hint.trim(),
      link: link.trim(),
      priority,
      fulfilled: false,
      createdBy: user.uid,
      createdByName: user.displayName,
      timestamp: serverTimestamp(),
    });
    setTitle('');
    setHint('');
    setLink('');
    setPriority(PRIORITIES[0]);
    setOpen(false);
  };

  const toggle = async (wish: Wish) => {
    await updateDoc(wishlistDoc(user.coupleId, wish.id), { fulfilled: !wish.fulfilled });
  };

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(backTo)}>←</button>
        <h1 style={styles.title}>Hediye Listesi</h1>
      </div>

      <div className="feature-page">
        <section className="feature-hero-card">
          <p className="eyebrow">Hediye ipuçları</p>
          <h2>Tahmin oyunu değil, tatlı ipucu sistemi.</h2>
          <span>İstediğin şeyi doğrudan değilse bile ipucu olarak bırak. Partnerin fikir kaybetmesin.</span>
          <button className="feature-primary" onClick={() => setOpen(true)}>İpucu bırak</button>
        </section>

        <section className="wish-list">
          {wishes.map(wish => (
            <article key={wish.id} className={`wish-card ${wish.fulfilled ? 'done' : ''}`}>
              <div>
                <small>{wish.priority} · {wish.createdByName}</small>
                <h3>{wish.title}</h3>
                {wish.hint && <p>{wish.hint}</p>}
                {wish.link && <a href={wish.link} target="_blank" rel="noreferrer">Linki aç</a>}
              </div>
              <div className="wish-actions">
                <button onClick={() => toggle(wish)}>{wish.fulfilled ? 'Alındı' : 'Not aldım'}</button>
                {wish.createdBy === user.uid && <button onClick={() => deleteDoc(wishlistDoc(user.coupleId, wish.id))}>Sil</button>}
              </div>
            </article>
          ))}
          {wishes.length === 0 && <div className="empty-state">Henüz hediye ipucu yok. İlk ipucunu bırak.</div>}
        </section>
      </div>

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2>Hediye ipucu</h2>
            <p className="modal-sub">Net yazabilir ya da sadece yön gösterebilirsin.</p>
            <input placeholder="Ne olsun?" value={title} onChange={e => setTitle(e.target.value)} style={{ marginBottom: 10 }} />
            <select value={priority} onChange={e => setPriority(e.target.value)} style={styles.select}>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
            <input placeholder="Link (opsiyonel)" value={link} onChange={e => setLink(e.target.value)} style={{ marginBottom: 10 }} />
            <textarea placeholder="İpucu" value={hint} onChange={e => setHint(e.target.value)} style={{ marginBottom: 12 }} />
            <button className="btn btn-primary" onClick={add}>Kaydet</button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
  select: {
    width: '100%',
    marginBottom: 10,
    padding: '14px 16px',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.09)',
    background: 'rgba(255,255,255,0.045)',
    color: 'white',
    fontFamily: 'Inter, sans-serif',
  },
};
